import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuItem,
  MenuList,
  Tag,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useVirtualizer } from '@tanstack/react-virtual'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import MenuButton from 'components/menu-button'
import { useUserContext } from 'contexts/user-context'
import { isAdminUser } from 'utils/auth-utils'

import theme from 'theme'

import type { Customer } from 'types'

type Props = {
  onSelect?: () => void
}

const ClubMenu = ({ onSelect }: Props) => {
  const navigate = useNavigate()
  const isLargeViewport = useBreakpointValue({ base: false, lg: true })
  const { customers, user, selectedCustomer, setSelectedCustomer } = useUserContext()
  const [searchTerm, setSearchTerm] = useState('')

  const filteredCustomers =
    customers
      ?.filter(customer => customer.customerName.toUpperCase().includes(searchTerm.trim().toUpperCase()))
      .sort((a, b) => a.customerName.toLowerCase().localeCompare(b.customerName.toLowerCase())) ?? []
  const isAdmin = isAdminUser(user)

  const parentRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const virtualizer = useVirtualizer({
    count: filteredCustomers?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 10,
  })

  const selectFn = (customer: Customer) => {
    setSelectedCustomer(customer)
    virtualizer.scrollToIndex(0)
    navigate('/')
    if (onSelect) {
      onSelect()
    }
  }

  if (!customers?.length) {
    return null
  }

  return (
    <Flex>
      <Menu matchWidth={!isLargeViewport} placement="bottom-end" initialFocusRef={searchRef}>
        <MenuButton
          title="Pat Cummins"
          // title={selectedCustomer?.customerName ?? ''}
          // subTitle="Club"
          subTitle="ATHLETE"
          w="100%"
          css={{
            '&[data-active]': {
              background: theme.colors.gray['100'],
            },
          }}
        />
        <MenuList minW={{ base: '300px', lg: '450px' }} css={{ boxShadow: theme.shadows.md }}>
          {customers.length > 10 && (
            <InputGroup>
              <Input
                ref={searchRef}
                mx={3}
                mb={2}
                placeholder="Search clubs..."
                onChange={e => setSearchTerm(e.target.value)}
                onBlurCapture={() => searchRef.current?.focus()}
              />
              <InputRightElement mr={4}>
                <FontAwesomeIcon icon={['far', 'search']} />
              </InputRightElement>
            </InputGroup>
          )}
          <Box maxH="500px" overflowY="auto" ref={parentRef}>
            <Box h={`${virtualizer.getTotalSize()}px`} w="100%" position="relative">
              {filteredCustomers &&
                virtualizer.getVirtualItems().map(virtualItem => {
                  const customer = filteredCustomers[virtualItem.index]
                  // https://github.com/moroshko/autosuggest-highlight
                  const matches = match(customer.customerName, searchTerm, { insideWords: true })
                  const parts = parse(customer.customerName, matches)

                  return (
                    <MenuItem
                      key={virtualItem.key}
                      onClick={() => selectFn(customer)}
                      bg={
                        customer.customerGuid === selectedCustomer?.customerGuid ? theme.colors.blue['50'] : undefined
                      }
                      position="absolute"
                      top={0}
                      left={0}
                      h={`${virtualItem.size}px`}
                      transform={`translateY(${virtualItem.start}px)`}
                      _hover={{ bg: theme.colors.gray['100'] }}
                    >
                      <Text
                        mr={2}
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        title={customer.customerName}
                      >
                        {parts.map((part, index) => (
                          <span
                            key={index}
                            style={{
                              fontWeight: part.highlight ? 700 : 400,
                            }}
                          >
                            {part.text}
                          </span>
                        ))}
                      </Text>
                      {isAdmin && (
                        <Tag
                          size="sm"
                          variant="solid"
                          backgroundColor={theme.realm[customer.realmID ?? 0] ?? theme.realm[0]}
                          fontSize="10px"
                          p="2px 4px"
                          mr={2}
                        >
                          {customer.realm?.name}
                        </Tag>
                      )}
                      {customer.customerGuid === selectedCustomer?.customerGuid && (
                        <FontAwesomeIcon icon={['fas', 'circle-check']} color={theme.colors.blue['400']} />
                      )}
                    </MenuItem>
                  )
                })}
            </Box>
          </Box>
        </MenuList>
      </Menu>
    </Flex>
  )
}

export default ClubMenu
